<template>
  <BaseDrawer
    v-if="product && initialValues"
    :is-open="isOpen"
    shrink
    @close="cancel"
  >
    <template #title>
      {{ product.name }}
    </template>
    <OrderItemForm
      :product="product"
      :initial-values="initialValues"
      :is-editing="isEditing"
      @submit="handleSubmit"
      @delete="handleDelete"
    />
  </BaseDrawer>
</template>

<script setup lang="ts">
import type { CurrentOrderOrderItemModifier } from "@/composables/useCurrentOrder"
import type { Product } from "@/services/sharedTypes/products"
import type {
  OrderItemFormInput,
  OrderItemFormOutput,
} from "@/forms/orderItemForm"
import { useConfirmDialog } from "@vueuse/core"
import { useProducts } from "@/services/products/listProducts"
import { useProductModifers } from "@/services/product-modifiers/listProductModifiers"

const {
  reveal,
  isRevealed: isOpen,
  confirm,
  cancel,
  onReveal,
} = useConfirmDialog<
  | {
      product: Product
    }
  | {
      orderItemIndex: number
    },
  {
    product: Product
    params: OrderItemFormOutput
  }
>()

defineExpose({
  reveal,
})

const product = ref<Product | null>(null)
const orderItemIndex = ref<number>()
const initialValues = ref<OrderItemFormInput>()
const isEditing = ref<boolean>()

const { currentOrder, removeItem } = useCurrentOrder()
const { getByIds } = useProductModifers()
const { allProducts } = useProducts()
onReveal((params) => {
  if ("orderItemIndex" in params) {
    orderItemIndex.value = params.orderItemIndex
    isEditing.value = true
    const orderItem =
      currentOrder.value.orderItems[params.orderItemIndex]

    if (!allProducts.value || !orderItem.product) {
      product.value = null
      return
    }

    const orderItemProduct = orderItem.product
    const _product = allProducts.value.find(
      (p) => p.id === orderItemProduct.id
    )

    if (!_product) return
    product.value = _product

    initialValues.value = {
      note: orderItem.note || "",
      modifiers: orderItem.modifiers?.map((m) => m) || [],
      quantity: orderItem.quantity,
    }
  } else {
    product.value = params.product

    const modifierGroups = getByIds(
      product.value.modifierGroupIds
    )

    initialValues.value = {
      quantity: 1,
      note: "",
      modifiers: modifierGroups.value.flatMap((group) => {
        const groupHasNoDefault = group.options.every(
          (option) => !option.isSelected
        )
        const groupIsSingleSelect =
          group.maxOptions === 1 && group.minOptions === 1

        return group.options.reduce(
          (modifiers, option, optionIndex) =>
            option.isSelected ||
            (groupHasNoDefault &&
              groupIsSingleSelect &&
              optionIndex === 0)
              ? [
                  ...modifiers,
                  {
                    id: option.id,
                    groupId: group.id,
                    label: option.label,
                    priceOffset: option.priceOffset,
                  },
                ]
              : modifiers,
          [] as CurrentOrderOrderItemModifier[]
        )
      }),
    }
  }
})

const handleSubmit = (params: OrderItemFormOutput) => {
  if (!product.value) return
  confirm({ params, product: product.value })
}

const handleDelete = () => {
  if (orderItemIndex.value === undefined) return
  removeItem(orderItemIndex.value)
  cancel()
}
</script>
